import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import utils, { isUndefined } from 'utils';
import { Button } from 'ncoded-component-library';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import ChecklistIcon from 'icons/Checklist.icon';
import StillForSaleSection from './components/StillForSaleSection';
import NoLongerForSaleSection from './components/NoLongerForSaleSection';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { PulseCheckValues } from 'api/portfolio/seller';
import api from 'api';
import DealContext from '../../providers/Deal/Deal.context';
import { DEAL_STAGE } from 'models/DealRoom';

import './BrokerPulsCheckDrawer.styles.scss';
import './BrokerPulsCheckDrawer.styles.responsive.scss';

type BrokerPulsCheckDrawerProps = {
  className?: string;
};

const BrokerPulsCheckDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  BrokerPulsCheckDrawerProps
> = (props, ref) => {
  const { className } = props;

  const {
    dealRoom: { _id },
    setDealRoom,
  } = useContext(DealContext);

  const { popServerError } = useContext(PopNotificationsContext);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const classes = classNames('broker-puls-check-drawer', className);

  const onSubmit = useCallback(
    async (
      values: PulseCheckValues & {
        isStillForSale: boolean;
        skipMessage: boolean;
      },
    ) => {
      try {
        const {
          isStillForSale,
          skipMessage,
          message,
          stage,
          settlementDate,
          settlementPrice,
          offerStatus,
          contractStatus,
          isExternalUnderOfferLead,
          underOfferLeadId,
        } = values;

        let requestData: PulseCheckValues;

        if (isStillForSale) {
          requestData = {
            stage: DEAL_STAGE.LIVE,
            ...(!skipMessage &&
              message && {
                message,
              }),
          };
        } else {
          requestData = {
            stage,
            ...(!skipMessage &&
              message && {
                message,
              }),
            ...((stage === DEAL_STAGE.SOLD ||
              stage === DEAL_STAGE.COMPLETE) && {
              settlementDate,
              settlementPrice: utils.getIntegerFromDottedNumber(
                (settlementPrice || 0).toString(),
              ),
            }),
            ...(stage === DEAL_STAGE.UNDER_OFFER && {
              offerStatus,
              isExternalUnderOfferLead,
              underOfferLeadId,
            }),
            ...(stage === DEAL_STAGE.UNDER_CONTRACT && {
              contractStatus,
              isExternalUnderOfferLead,
              underOfferLeadId,
            }),
          };
        }

        const { data } = await api.portfolio.seller.submitPulseCheck(
          _id,
          requestData,
        );

        drawer.setOpen(false);
        setDealRoom((prev) => ({ ...prev, ...data }));
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, drawer, popServerError, setDealRoom],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      <Form
        onSubmit={onSubmit}
        render={({
          handleSubmit,
          values: { isStillForSale },
          form: { change },
        }) => (
          <form onSubmit={handleSubmit}>
            {isUndefined(isStillForSale) ? (
              <>
                <p className="steps">
                  {t('stepsCurrentOf', { currentStep: 1, steps: 2 })}
                </p>

                <p className="title">{t('brokerPulseCheckIntialTitle')}</p>

                <Button
                  icon={CheckcircleIcon}
                  onClick={() => change('isStillForSale', true)}
                >
                  {t('yesStillForSale')}
                </Button>
                <Button
                  icon={ChecklistIcon}
                  variant="outline"
                  onClick={() => change('isStillForSale', false)}
                >
                  {t('noLongerForSale')}
                </Button>
              </>
            ) : isStillForSale ? (
              <StillForSaleSection />
            ) : (
              <NoLongerForSaleSection />
            )}
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(BrokerPulsCheckDrawer);
